import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './AspspsList.css';
import { getEnvironmentParams } from '../utils/auth';
import { apiUrl, envConfig, getCurrentEnv } from '../config/envConfig';
import PageLayout from './common/PageLayout';
import { useTranslation } from 'react-i18next';
import InfoIcon from '../assets/icons/info.js';



function AspspsList() {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCountry, setSelectedCountry] = useState('SE'); // Default to Sweden
    const [banks, setBanks] = useState([]);
    const [filteredBanks, setFilteredBanks] = useState([]);
    const [bankDropdownOpen, setBankDropdownOpen] = useState(false);
    const bankDropdownRef = useRef(null);
    const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
    const countryDropdownRef = useRef(null);
    const [selectedBank, setSelectedBank] = useState(null);
    const [bankOptions, setBankOptions] = useState([]);
    const [selectedBankOption, setSelectedBankOption] = useState(null);
    const [bankOptionsDropdownOpen, setBankOptionsDropdownOpen] = useState(false);
    const bankOptionsDropdownRef = useRef(null);
    const [socialSecurityNumber, setSocialSecurityNumber] = useState('');
    const [ssnError, setSsnError] = useState('');
    const [consentError, setConsentError] = useState('');

    // Updated countries array with flag image codes
    const countries = [
        { code: 'SE', name: 'Sweden', flagCode: 'se' },
        { code: 'NO', name: 'Norway', flagCode: 'no' },
        { code: 'DK', name: 'Denmark', flagCode: 'dk' },
        { code: 'FI', name: 'Finland', flagCode: 'fi' }
    ];



    // Fetch banks from API
    useEffect(() => {
        const fetchBanks = async () => {
            try {
                setLoading(true);
                
                const params = getEnvironmentParams();
                if (!params.token) {
                    throw new Error('No authentication token found');
                }

                const response = await fetch(
                    `${apiUrl}/aspsps?Country=${selectedCountry}`, 
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${params.token}`
                        }
                    }
                );

                if (!response.ok) {
                    throw new Error('Failed to fetch banks');
                }

                const data = await response.json();
                if (Array.isArray(data)) {
                    setBanks(data);
                    setFilteredBanks(data);
                }
                setLoading(false);
            } catch (err) {
                console.error('Error fetching banks:', err);
                setLoading(false);
            }
        };

        fetchBanks();
    }, [selectedCountry]);

    // Filter banks based on search term
    useEffect(() => {
        if (searchTerm.trim() === '') {
            setFilteredBanks(banks);
            return;
        }

        const filtered = banks.filter(bank => 
            bank.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredBanks(filtered);
        }, [searchTerm, banks]);

    // Handle country change
const handleCountryChange = (countryCode) => {
    setSelectedCountry(countryCode);
    setSearchTerm(''); // Clear selected bank text
    setFilteredBanks([]); // Optionally clear filtered banks immediately
    setBankDropdownOpen(false); // Close the bank dropdown if open
    setCountryDropdownOpen(false); // Close the country dropdown if open
    setSelectedBank(null); // Reset selected bank state
    setSelectedBankOption(null); // Reset additional option selection
    setBankOptions([]); // Clear previous bank options
    setSocialSecurityNumber(''); // Clear SSN when country changes
    setSsnError(''); // Clear SSN error when country changes
    setConsentError(''); // Clear consent error when country changes
};

    // Add new function to fetch bank options
    const fetchBankOptions = async (bicFi) => {
        try {
            const params = getEnvironmentParams();
            if (!params.token) {
                throw new Error('No authentication token found');
            }

            const response = await fetch(`${apiUrl}/aspsps/${bicFi}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${params.token}`
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch bank options');
            }

            const data = await response.json();
            setBankOptions(data);
        } catch (error) {
            console.error('Error fetching bank options:', error);
            setBankOptions([]);
        }
    };

    // Modify the bank selection handler
    const handleBankSelect = (bank) => {
        setSearchTerm(bank.name);
        setSelectedBank(bank);
        setBankDropdownOpen(false);
        setSelectedBankOption(null);
        setConsentError(''); // Clear consent error when bank changes
        fetchBankOptions(bank.bicFi);
    };

    // Close dropdown on outside click
    useEffect(() => {
        function handleClickOutside(event) {
            if (bankDropdownRef.current && !bankDropdownRef.current.contains(event.target)) {
                setBankDropdownOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        function handleClickOutside(event) {
            if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) {
                setCountryDropdownOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        function handleClickOutside(event) {
            if (bankOptionsDropdownRef.current && !bankOptionsDropdownRef.current.contains(event.target)) {
                setBankOptionsDropdownOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const selectedCountryData = countries.find(c => c.code === selectedCountry);

    const getSsnPlaceholder = () => {
        switch (selectedCountry) {
            case 'DK':
                return 'DDMMYY-SSSS';
            case 'SE':
                return 'YYYYMMDD-XXXX';
            case 'NO':
                return 'DDMMYYIIICC';
            case 'FI':
                return 'DDMMYYCZZZQ';
            case 'DE':
                return 'XXXXXXXXXXX';
            default:
                return 'XXXXXXXXXXX';
        }
    };

    // Validate SSN format based on country
    const validateSsn = useCallback((ssn, country) => {
        if (!ssn || ssn.trim() === '') {
            return { isValid: false, error: '' };
        }

        const trimmedSsn = ssn.trim();
        let isValid = false;
        let pattern = null;

        switch (country) {
            case 'DK':
                pattern = /^\d{6}-\d{4}$/;
                isValid = pattern.test(trimmedSsn);
                break;
            case 'SE':
                pattern = /^\d{8}-[A-Za-z0-9]{4}$/;
                isValid = pattern.test(trimmedSsn);
                break;
            case 'NO':
                pattern = /^\d{11}$/;
                isValid = pattern.test(trimmedSsn);
                break;
            case 'FI':
                pattern = /^\d{6}[A-Za-z0-9]{5}$/;
                isValid = pattern.test(trimmedSsn);
                break;
            default:
                isValid = trimmedSsn.length > 0;
                break;
        }

        return {
            isValid,
            error: isValid ? '' : (t('aspspsSsnInvalid') || 'Invalid Social security number')
        };
    }, [t]);

    const requireAdditionalInfo = selectedBank;

    // Validate SSN when it changes
    useEffect(() => {
        if (!requireAdditionalInfo) {
            setSsnError('');
            return;
        }

        if (socialSecurityNumber.trim() === '') {
            setSsnError('');
            return;
        }

        const validation = validateSsn(socialSecurityNumber, selectedCountry);
        setSsnError(validation.error);
    }, [socialSecurityNumber, selectedCountry, requireAdditionalInfo, validateSsn]);

    const ssnValidation = requireAdditionalInfo ? validateSsn(socialSecurityNumber, selectedCountry) : { isValid: true, error: '' };
    const isAdditionalInfoValid = !requireAdditionalInfo || (socialSecurityNumber.trim() !== '' && ssnValidation.isValid);
    const canProceed = Boolean(selectedBank) && Boolean(isAdditionalInfoValid);
    const [isCreatingConsent, setIsCreatingConsent] = useState(false);

    const handleNextClick = async () => {
        if (!canProceed || isCreatingConsent) {
            return;
        }

        // Clear any previous error
        setConsentError('');

        // Store bank and country data in sessionStorage
        if (selectedBank) {
            const countryData = countries.find(c => c.code === selectedCountry);
            const sanizeSSn = socialSecurityNumber.replace(/-/g, '');
            const bankDataWithExtras = {
                ...selectedBank,
                selectedOption: selectedBankOption ? { id: selectedBankOption.id, name: selectedBankOption.name } : null,
                socialSecurityNumber: sanizeSSn || null
            };
            sessionStorage.setItem('selectedBank', JSON.stringify(bankDataWithExtras));
            sessionStorage.setItem('selectedCountry', JSON.stringify(countryData));
        }

        setIsCreatingConsent(true);
        try {
            // Get user IP and user agent
            const userAgent = navigator.userAgent;
            let userIp = '127.0.0.1';
            
            try {
                const ipResponse = await fetch('https://api.ipify.org?format=json');
                const ipData = await ipResponse.json();
                userIp = ipData.ip;
            } catch (err) {
                console.error('Error fetching IP address:', err);
            }

            // Get banking info from sessionStorage (just set above)
            const bankData = JSON.parse(sessionStorage.getItem('selectedBank'));

            // Make the consent API call
            const params = getEnvironmentParams();
            if (!params.token) {
                throw new Error('No authentication token found');
            }

            const headers = {
                'Content-Type': 'application/json',
                'BicFi': bankData?.bicFi || 'ESSESESS',
                'Psu-Id': bankData?.socialSecurityNumber,
                'Psu-Ip-Address': userIp,
                'Psu-User-Agent': userAgent,
                'IsSameDevice': 'true',
                'Redirect-Uri': `${envConfig[getCurrentEnv()].redirectUri}`,
                'Authorization': `Bearer ${params.token}`,
                'Tpp-Redirect-Preferred': false,
            };

            const response = await fetch(`${apiUrl}/consents`, {
                method: 'POST',
                headers: headers,
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            
            // Store consent data in session storage for later use
            sessionStorage.setItem('consentData', JSON.stringify(data));

            // Check if scaOAuthLink exists and redirect to it
            if (data.scaOAuthLink) {
                window.location.href = data.scaOAuthLink;
                return;
            }

            // If no scaOAuthLink, proceed with normal navigation
            navigate('/bank-connection');
        } catch (err) {
            console.error('Error creating consent:', err);
            // Set error message and stay on the same page
            setConsentError(t('bankConnErrorMessage') || 'Something went wrong. Please try again.');
        } finally {
            setIsCreatingConsent(false);
        }
    };

    const navigationProps = {
        previousPath: "/kyc-complete",
        onNext: handleNextClick,
        nextDisabled: !canProceed || isCreatingConsent
    };

    if (loading) {
        return <div className="loading">{t('aspspsLoading')}</div>;
    }

    return (
        <PageLayout currentStep={2} navigationProps={navigationProps} 
        infoText="aspspsInfoText">
            <div className="widget-content">
                <h1 className="main-title">{t('aspspsTitle')}</h1>
                <p className="main-subtitle">
                    {t('aspspsSubtitle1')}
                    <br />
                    {t('aspspsSubtitle2')}
                </p>

                <div className="form-group">
                    <label htmlFor="country-select">{t('aspspsCountryLabel')}</label>
                    <div className="custom-country-select-wrapper" ref={countryDropdownRef}>
                        <button
                            type="button"
                            id="country-select"
                            className="custom-country-select-header"
                            onClick={() => setCountryDropdownOpen(!countryDropdownOpen)}
                            aria-expanded={countryDropdownOpen}
                            aria-haspopup="true"
                            aria-controls="country-select-options"
                        >
                            <span className={`fi fi-${selectedCountryData.flagCode}`}></span>
                            <span>{t(selectedCountryData.name)}</span>
                            <span className="select-arrow">▼</span>
                        </button>
                        {countryDropdownOpen && (
                            <div className="custom-country-select-options" id="country-select-options">
                                {countries.map((country, index) => (
                                    <button
                                        type="button"
                                        key={country.code}
                                        className={`custom-country-select-option${selectedCountry === country.code ? ' selected' : ''}`}
                                        onClick={() => {
                                            handleCountryChange(country.code);
                                        }}
                                    >
                                        <span className={`fi fi-${country.flagCode}`}></span>
                                        <span>{t(country.name)}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="form-group">
                <div className="form-group">
                            <label htmlFor="ssn-input">
                                {t('aspspsSsnLabel')}
                                <span
                                    className="tooltip-icon"
                                    aria-label={t('aspspsSsnTooltip')}
                                    data-tooltip={t('aspspsSsnTooltip')}
                                >
                                    i
                                </span>
                            </label>
                            <input
                                id="ssn-input"
                                type="text"
                                inputMode="numeric"
                                autoComplete="off"
                                className={`ssn-input ${ssnError ? 'ssn-input-error' : ''}`}
                                value={socialSecurityNumber}
                                onChange={(e) => setSocialSecurityNumber(e.target.value)}
                                placeholder={getSsnPlaceholder()}
                                required={requireAdditionalInfo}
                                aria-invalid={ssnError ? 'true' : 'false'}
                                aria-describedby={ssnError ? 'ssn-error' : undefined}
                            />
                            <div className='infoTextStyles'>
                                                <InfoIcon />
                                                <span>{t('aspspsSsnInfo')}</span>
                            </div>

                            {ssnError && (
                                <span id="ssn-error" className="ssn-error-message" role="alert">
                                    {ssnError}
                                </span>
                            )}

                        </div>
                    <label htmlFor="bank-select">{t('aspspsBankLabel')}</label>
                    <div className="custom-bank-select-wrapper" ref={bankDropdownRef}>
                        <button
                            type="button"
                            id="bank-select"
                            className="custom-bank-select-header"
                            onClick={() => setBankDropdownOpen(!bankDropdownOpen)}
                            aria-expanded={bankDropdownOpen}
                            aria-haspopup="true"
                            aria-controls="bank-select-options"
                        >
                            {searchTerm && (
                                <>
                                    <img
                                        src={filteredBanks.find(b => b.name === searchTerm)?.logoUrl}
                                        alt={searchTerm}
                                        className="bank-logo"
                                        style={{ width: 24, height: 24, marginRight: 8, verticalAlign: 'middle' }}
                                    />
                                    <span>{searchTerm}</span>
                                </>
                            )}
                            {!searchTerm && <span>{t('aspspsSelectYourBank')}</span>}
                            <span className="select-arrow">▼</span>
                        </button>
                        {bankDropdownOpen && (
                            <div className="custom-bank-select-options" id="bank-select-options">
                                {banks.map((bank, index) => (
                                    <button
                                        type="button"
                                        key={`${bank.name}-${bank.country}`}
                                        className={`custom-bank-select-option${selectedBank && selectedBank.name === bank.name ? " selected" : ""}`} 
                                        onClick={() => handleBankSelect(bank)}
                                    >
                                        <img
                                            src={bank.logoUrl}
                                            alt={bank.name}
                                            className="bank-logo"
                                            style={{ width: 24, height: 24, marginRight: 8, verticalAlign: 'middle' }}
                                        />
                                        <span>{bank.name}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Add new bank options dropdown */}
                {selectedBank && bankOptions.length > 0 && (
                        <div className="form-group">
                            <label htmlFor="bank-options-select">{t('aspspsSelectOption')}</label>
                            <div className="custom-bank-select-wrapper" ref={bankOptionsDropdownRef}>
                                <button
                                    type="button"
                                    id="bank-options-select"
                                    className="custom-bank-select-header"
                                    onClick={() => setBankOptionsDropdownOpen(!bankOptionsDropdownOpen)}
                                    aria-expanded={bankOptionsDropdownOpen}
                                    aria-haspopup="true"
                                    aria-controls="bank-options-select-options"
                                >
                                    {selectedBankOption ? selectedBankOption.name : t('aspspsSelectOption')}
                                    <span className="select-arrow">{bankOptionsDropdownOpen ? "▲" : "▼"}</span>
                                </button>
                                {bankOptionsDropdownOpen && (
                                    <div className="custom-bank-select-options" id="bank-options-select-options">
                                        {bankOptions.map((option, index) => (
                                            <button
                                                type="button"
                                                key={option.id}
                                                className={`custom-bank-select-option${selectedBankOption && selectedBankOption.id === option.id ? " selected" : ""}`}
                                                onClick={() => {
                                                    setSelectedBankOption(option);
                                                    setBankOptionsDropdownOpen(false);
                                                }}
                                            >
                                                {option.name}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                )}

                {/* Error message for consent creation failure */}
                {consentError && (
                    <div className="consent-error-message" role="alert">
                        {consentError}
                    </div>
                )}
            </div>
        </PageLayout>
    );
}

export default AspspsList;